import DOMPurify from 'dompurify';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Link } from '../../../../../CommonComponents/OPL/Link/Link';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { selectHelpStyle } from '../../../../../AdviserArea/AdviserAreaSelector';

const getDataFromADV = () => ({
  ...ADV.Help,
  sampleClients: ADV.entities.getSampleClients(),
  demoURI: ADV.settings.endpoints.demo,
});

interface HelpModalProps {
  onClose(): void;
  onDemo(): void;
  onContact(): void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ onClose, onDemo, onContact }) => {
  const { register, handleSubmit } = useForm<{ search: string }>({ defaultValues: { search: '' } });

  const helpStyle = useSelector(selectHelpStyle);

  const { title, description, sampleClients, demoURI } = getDataFromADV();

  const handleSearchSubmit = handleSubmit(({ search }) => {
    window.open(
      `${ADV.info.resourceCenterUrl}global-search/${encodeURIComponent(search)}`,
      '_blank',
    );
  });

  return (
    <Modal size={title && description ? 'large' : 'small'} isOpen onRequestClose={onClose}>
      <ModalHeader>Help</ModalHeader>

      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <Box display="flex">
          {title && (
            <>
              <Box flex={1}>
                <Text as="h4" fontSize="h4" mb="16">
                  {title}
                </Text>

                <Text
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                />
              </Box>

              <Box width={2} mx="24" bg="neutral.200" />
            </>
          )}

          <Box as="form" flex={1} onSubmit={handleSearchSubmit}>
            {helpStyle !== 'Voya' && (
              <Input
                {...register('search', { required: true })}
                aria-label="Search the Resource Center"
                placeholder="Search the Resource Center"
              />
            )}

            <Box display="flex" flexDirection="column" alignItems="flex-start">
              {helpStyle !== 'Voya' && (
                <Link isExternal href={`${ADV.info.resourceCenterUrl}financial-planning`} mt="16">
                  Resource Center
                </Link>
              )}

              <Button variant="link" onClick={onDemo} mt="16">
                Watch Demo
              </Button>

              {helpStyle !== 'Voya' && (
                <Button variant="link" onClick={onContact} mt="16">
                  Contact Orion Planning
                </Button>
              )}

              {sampleClients?.map(({ id, firstName, lastName }) => (
                <Link key={id} isExternal href={`${demoURI}&masterSampleClientId=${id}`} mt="16">
                  {`${firstName} ${lastName}`}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </ModalBody>
    </Modal>
  );
};
